// react
import React, { useState, useMemo } from 'react';

// mui
import { Button, Stack, TextField } from '@mui/material';

// components
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { EmailPreview } from '@/pwa/molecules/EmailPreview/EmailPreview';

export interface ClientReferralEmailPreviewPageProps {
  onBack?: () => void;
  clientFirstName: string;
  inviterName: string;
  recipientEmail: string;
  subject: string;
  emailHtml: string;
  onSend: (personalizedMessage: string) => void | Promise<void>;
  isSending?: boolean;
}

export const ClientReferralEmailPreviewPage = ({
  onBack,
  clientFirstName,
  inviterName,
  recipientEmail,
  subject,
  emailHtml,
  onSend,
  isSending = false,
}: ClientReferralEmailPreviewPageProps) => {
  const [personalizedMessage, setPersonalizedMessage] = useState('');

  const renderedHtml = useMemo(() => {
    let html = emailHtml;

    // Replace greeting with client first name.
    // The Go template renders either "Hello {name}," or "Hi there," with
    // surrounding whitespace inside the <div>.
    if (clientFirstName) {
      html = html.replace(
        /(\bHi there\s*,|\bHello\s*,)/,
        `Hello ${clientFirstName},`,
      );
    }

    // Inject a personalized message paragraph into the email HTML.
    // The Go backend can't render a placeholder (text/template interprets
    // {{}} syntax), so we inject the paragraph client-side before the
    // "KareHero can offer the following support" paragraph.
    if (personalizedMessage) {
      const msgParagraph =
        '<tr><td align="left" style="font-size:0px;padding:10px 20px;word-break:break-word">' +
        '<div style="font-family:apple-system,system-ui,BlinkMacSystemFont,Helvetica Neue,Helvetica,Arial,Verdana,sans-serif;font-size:16px;line-height:24px;text-align:left;color:#2b2b2b">' +
        `<strong>From ${inviterName}</strong><br />"${personalizedMessage}"` +
        '</div></td></tr>';

      // Insert before the "KareHero can offer the following support" paragraph
      const anchor = 'KareHero can offer the following support';
      const anchorIdx = html.indexOf(anchor);
      if (anchorIdx !== -1) {
        // Find the start of the <tr> containing the anchor
        const trStart = html.lastIndexOf('<tr>', anchorIdx);
        if (trStart !== -1) {
          html = html.slice(0, trStart) + msgParagraph + html.slice(trStart);
        }
      }
    }

    return html;
  }, [emailHtml, clientFirstName, inviterName, personalizedMessage]);

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title='Client Referral - Review Invitation'
            description='Review the email invitation which will be sent to your client. You can optionally add a personalised message.'
          />
        }
        contentSlot={
          <Stack spacing={3}>
            <TextField
              label='Personalised Message'
              placeholder='Hi Joe, I hope this referral to KareHero finds you well.'
              value={personalizedMessage}
              onChange={(e) => setPersonalizedMessage(e.target.value)}
              fullWidth
              disabled={isSending}
            />

            <EmailPreview
              recipientEmail={recipientEmail}
              subject={subject}
              emailHtml={renderedHtml}
            />

            <Button
              variant='contained'
              fullWidth
              onClick={() => onSend(personalizedMessage)}
              disabled={isSending}
            >
              {isSending ? 'Sending…' : 'Send'}
            </Button>
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

ClientReferralEmailPreviewPage.displayName = 'ClientReferralEmailPreviewPage';
export default ClientReferralEmailPreviewPage;
